// Editor dos "Dados de exemplo" (§4.5): sobrescreve os valores de
// exemplo de cada variável do contrato para pré-visualizar o template
// com dados próprios. As edições são LOCAIS — valem só para o preview,
// não persistem no template — e podem ser restauradas ao padrão do
// contrato. O JSON reconstruído (payload resultante) atualiza ao vivo.
import { RotateCcwIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { NotificationTemplateVariableType } from "@/types/notification-center"

import {
  applySampleOverrides,
  buildExampleObjectFromVariables,
  getSampleVariableKey,
} from "../template-editor-utils"
import { JsonViewer } from "./json-viewer"

const editorPrefix =
  "pages.notificationCenter.templateEditorPage"

type TemplateSampleDataEditorProps = {
  variables: NotificationTemplateVariableType[]
  // Overrides por chave da variável (getSampleVariableKey).
  overrides: Record<string, string>
  onChange: (key: string, value: string) => void
  onReset: () => void
}

export const TemplateSampleDataEditor = ({
  variables,
  overrides,
  onChange,
  onReset,
}: TemplateSampleDataEditorProps) => {
  const { t } = useTranslation()
  const hasOverrides = Object.keys(overrides).length > 0
  const effectiveVariables = applySampleOverrides(
    variables,
    overrides
  )

  return (
    <div
      className="space-y-3"
      data-testid="sample-data-editor"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {t(`${editorPrefix}.sampleDataEditor.hint`)}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!hasOverrides}
          onClick={onReset}
        >
          <RotateCcwIcon
            className="mr-1 size-4"
            aria-hidden="true"
          />
          {t(`${editorPrefix}.sampleDataEditor.reset`)}
        </Button>
      </div>
      <div className="grid gap-4 @2xl:grid-cols-2">
        {/* Campos editáveis: um input por variável do contrato. */}
        <ul className="space-y-2.5">
          {variables.map((variable) => {
            const key = getSampleVariableKey(variable)
            const value = overrides[key] ?? variable.example
            const isEdited = overrides[key] !== undefined
            const inputId = `sample-${key}`
            return (
              <li key={key} className="space-y-1">
                <label
                  htmlFor={inputId}
                  className="flex flex-wrap items-center gap-2 text-xs"
                >
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px] text-violet-700">
                    {`{{:${key}}}`}
                  </code>
                  {isEdited && (
                    <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
                      {t(
                        `${editorPrefix}.sampleDataEditor.edited`
                      )}
                    </span>
                  )}
                </label>
                <Input
                  id={inputId}
                  value={value}
                  onChange={(event) =>
                    onChange(key, event.target.value)
                  }
                  aria-label={t(
                    `${editorPrefix}.sampleDataEditor.valueLabel`,
                    { name: key }
                  )}
                />
              </li>
            )
          })}
        </ul>
        {/* Payload resultante (reconstruído dos paths + edições). */}
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            {t(
              `${editorPrefix}.sampleDataEditor.resultTitle`
            )}
          </p>
          <JsonViewer
            value={buildExampleObjectFromVariables(
              effectiveVariables
            )}
            fileName="sample-data.json"
          />
        </div>
      </div>
    </div>
  )
}
